"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import Reveal from "@/components/Sections/Reveal";
import {
  ArrowLeft,
  Clock,
  Facebook,
  Twitter,
  Link as LinkIcon,
} from "lucide-react";
import Link from "next/link";
import SmoothImage from "@/components/SmoothImage";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import SameNews from "../components/SameNews";
import ScrollHeader from "@/components/Layouts/ScrollHeader";
import FooterContact from "@/components/Layouts/FooterContact";
import { PROJECT_INFO } from "@/types/projectConsts";
import { stripHTML, parseHtmlTextToJson } from "@/utils/stripHTML";
import footerContactData from "@/data/footerContactData.json";

export default function TinTucDetailClient({
  news,
  locale,
  dict,
  homepageData,
}: {
  news: any;
  locale: string;
  dict: any;
  homepageData: any;
}) {
  const handleShareClick = (platform: "fb" | "tw" | "copy") => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (platform === "fb") {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        "_blank",
      );
    } else if (platform === "tw") {
      window.open(
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
        "_blank",
      );
    } else if (platform === "copy") {
      navigator.clipboard.writeText(url);
      alert("Đã sao chép liên kết vào bộ nhớ tạm!");
    }
  };

  // Flatten add-on list
  const allAddOns = useMemo(
    () => [
      ...(Array.isArray(homepageData?.add_on_data)
        ? homepageData.add_on_data
        : []),
    ],
    [homepageData?.add_on_data],
  );

  const getAddOn = (key: string) => {
    return (
      allAddOns.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  const getAddOn2 = (key: string) => {
    return (
      homepageData?.add_on_data_2?.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  // Dynamically configure global PROJECT_INFO properties from project_info JSON API data
  const projectInfo = homepageData?.content;
  if (projectInfo) {
    try {
      const cleanContent = stripHTML(projectInfo).trim();
      const parsed = JSON.parse(cleanContent);
      if (parsed.projectName) PROJECT_INFO.projectName = parsed.projectName;
      if (parsed.address) PROJECT_INFO.address = parsed.address;
      if (parsed.hotline) PROJECT_INFO.hotline = parsed.hotline;
      if (parsed.email) PROJECT_INFO.email = parsed.email;
      if (parsed.website) PROJECT_INFO.website = parsed.website;
      if (parsed.zalo) PROJECT_INFO.zalo = parsed.zalo;
      if (parsed.messenger) {
        PROJECT_INFO.messenger = parsed.messenger;
        PROJECT_INFO.fb = parsed.messenger;
      } else if (parsed.fb) {
        PROJECT_INFO.messenger = parsed.fb;
        PROJECT_INFO.fb = parsed.fb;
      }
    } catch (e) {
      console.error("Failed to parse project_info JSON:", e);
    }
  }

  // Header options mappings
  const heroItem = getAddOn("herosection");
  let heroFormTitle = "ĐĂNG KÝ TƯ VẤN NGAY";
  let heroFormSubtitle = "Nhận thông tin mặt bằng & bảng giá mới nhất";
  let heroFormLayoutTypeTitle = "Sản phẩm quan tâm";
  let heroApptOptions: Array<{ label: string; value: string }> = [
    { label: "1PN", value: "1PN" },
    { label: "2PN", value: "2PN" },
    { label: "Studio", value: "Studio" },
  ];

  try {
    const rawContent = heroItem?.content
      ? stripHTML(heroItem.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed.heroFormTitle) heroFormTitle = parsed.heroFormTitle;
      if (parsed.heroFormSubtitle) heroFormSubtitle = parsed.heroFormSubtitle;
      if (parsed.heroFormLayoutTypeTitle)
        heroFormLayoutTypeTitle = parsed.heroFormLayoutTypeTitle;
      if (parsed.heroApptOptions && Array.isArray(parsed.heroApptOptions)) {
        heroApptOptions = parsed.heroApptOptions.map((item: any) => ({
          label: item.label || item.title || "Căn hộ",
          value: item.value || item.title || "Studio",
        }));
      }
    }
  } catch (e) {
    console.error("Failed to parse hero options:", e);
  }

  // Footer Data construction
  const footerItem = getAddOn("footer");

  const dynamicFooterContactData = useMemo(() => {
    let baseData: any = {};
    if (footerItem?.content) {
      const parsed = parseHtmlTextToJson<any>(footerItem.content);
      baseData = Array.isArray(parsed) ? parsed[0] || {} : parsed || {};
    }

    const projectLogoObj =
      baseData.projectLogo || footerContactData.middleColumn.projectLogo;
    const projectLogoSrc = footerItem?.picture
      ? appendImageUrlFromAPI({ src: footerItem.picture, size: "xl" })
      : "";

    const dev1Src = footerItem?.picture_2
      ? appendImageUrlFromAPI({ src: footerItem.picture_2, size: "xl" })
      : "";
    const dev2Src = footerItem?.picture_3
      ? appendImageUrlFromAPI({ src: footerItem.picture_3, size: "xl" })
      : "";

    const rawDevs =
      baseData.developers || footerContactData.middleColumn.developers || [];
    const developers = rawDevs.map((dev: any, idx: number) => ({
      role: dev.role || (idx === 0 ? "CHỦ ĐẦU TƯ" : "NHÀ PHÁT TRIỂN"),
      name: dev.name || "",
      src:
        (idx === 0
          ? dev1Src || dev.src
          : idx === 1
            ? dev2Src || dev.src
            : dev.src) || "",
    }));

    const partnerRecord = getAddOn2("footer-partners");

    let partnerJson: any = {};
    if (partnerRecord?.content) {
      const parsed = parseHtmlTextToJson<any>(partnerRecord.content);
      partnerJson = Array.isArray(parsed) ? parsed[0] || {} : parsed || {};
    }

    const partnerLogos = [
      partnerRecord?.picture,
      partnerRecord?.picture_2,
      partnerRecord?.picture_3,
      partnerRecord?.picture_4,
      partnerRecord?.picture_5,
      partnerRecord?.picture_6,
    ]
      .filter(Boolean)
      .map((src) => appendImageUrlFromAPI({ src, size: "s" }));

    const rawPartners =
      partnerJson.partners || footerContactData.middleColumn.partners || [];
    const partners = rawPartners.map((p: any, idx: number) => ({
      name: p.name || `Đối tác ${idx + 1}`,
      src: partnerLogos[idx] || p.src || "",
    }));

    return {
      tag: baseData.tag || footerContactData.tag,
      title: baseData.title || footerContactData.title,
      description: baseData.description || footerContactData.description,
      hotlineLabel: baseData.hotlineLabel || footerContactData.hotlineLabel,
      hotlineValue: baseData.hotlineValue || footerContactData.hotlineValue,
      emailLabel: baseData.emailLabel || footerContactData.emailLabel,
      emailValue: baseData.emailValue || footerContactData.emailValue,
      middleColumn: {
        developersTitle:
          baseData.developersTitle ||
          footerContactData.middleColumn.developersTitle,
        projectLogo: {
          name: projectLogoObj.name || "DRAGON HOME",
          src: projectLogoSrc,
        },
        developers,
        partnersTitle:
          partnerJson.partnersTitle ||
          footerContactData.middleColumn.partnersTitle,
        partners,
      },
      formTitle: baseData.formTitle || footerContactData.formTitle,
      formSecurityNote:
        baseData.formSecurityNote || footerContactData.formSecurityNote,
      formSuccessTitle:
        baseData.formSuccessTitle || footerContactData.formSuccessTitle,
      formSuccessDesc:
        baseData.formSuccessDesc || footerContactData.formSuccessDesc,
      nameLabel: baseData.nameLabel || footerContactData.nameLabel,
      namePlaceholder:
        baseData.namePlaceholder || footerContactData.namePlaceholder,
      phoneLabel: baseData.phoneLabel || footerContactData.phoneLabel,
      phonePlaceholder:
        baseData.phonePlaceholder || footerContactData.phonePlaceholder,
      needLabel: baseData.needLabel || footerContactData.needLabel,
      needOptions: baseData.needOptions || footerContactData.needOptions,
      formFooterNote:
        baseData.formFooterNote || footerContactData.formFooterNote,
      submitBtnText: baseData.submitBtnText || footerContactData.submitBtnText,
      submittingBtnText:
        baseData.submittingBtnText || footerContactData.submittingBtnText,
    };
  }, [footerItem, homepageData?.add_on_data_2]);

  return (
    <>
      <ScrollHeader
        apptOptions={heroApptOptions}
        formTitle={heroFormTitle}
        formSubtitle={heroFormSubtitle}
        formLayoutTypeTitle={heroFormLayoutTypeTitle}
      />

      <div className="min-h-screen bg-[#FAF8F5] text-[#193B2F] font-sans relative select-none">
        {/* PAPER TEXTURE OVERLAY */}
        <div className="absolute inset-0 z-0 pointer-events-none mix-blend-multiply opacity-50">
          <Image
            src="/images/background_2.png"
            alt="texture"
            fill
            sizes="100vw"
            priority
            className="object-cover object-center"
          />
        </div>

        <main className="relative z-10 pt-[84px] md:pt-[104px] pb-16">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <Reveal>
              <Link
                href={`/${locale}/tin-tuc`}
                className="inline-flex items-center gap-2 text-[#8A724C] hover:text-[#193B2F] font-sans font-bold text-xs uppercase tracking-wider mb-8 hover:-translate-x-1 transition-all"
              >
                <ArrowLeft className="w-4 h-4" />{" "}
                {dict.back_to_list || "Quay lại danh sách"}
              </Link>

              <div className="mb-10">
                <div className="inline-block px-3 py-1 bg-[#E5D4B2] text-[#193B2F] text-[11px] font-sans font-bold uppercase tracking-wider mb-4 shadow-sm border border-[#C9AB81]/40">
                  {news.loai_tin_tuc ||
                    dict.categories?.company ||
                    "Dragon Home"}
                </div>
                <h1 className="font-lora text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal text-[#193B2F] leading-tight mb-5">
                  {news.title}
                </h1>

                <div className="flex flex-wrap items-center gap-6 text-[#193B2F]/60 font-sans font-medium border-b border-[#193B2F]/10 pb-6 text-xs">
                  <div className="flex items-center gap-2 text-[#8A724C]">
                    <Clock className="w-4 h-4" />{" "}
                    {news.date_created
                      ? new Date(news.date_created).toLocaleDateString(
                          "vi-VN",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          },
                        )
                      : ""}
                  </div>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#8A724C]/40"></div>
                  <div className="uppercase tracking-wider font-semibold text-[#8A724C]">
                    {dict.editorial_board || "Ban biên tập Dragon Home"}
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="grid lg:grid-cols-[1fr_320px] gap-12">
              <Reveal delay={200}>
                {news.picture && (
                  <div className="relative aspect-[21/10] overflow-hidden mb-10 bg-[#EAE5DB] shadow-md border border-[#193B2F]/10">
                    <SmoothImage
                      src={appendImageUrlFromAPI({
                        src: news.picture,
                        size: "2k",
                      })}
                      alt={news.title}
                      fill
                      sizes="(max-width: 1024px) 100vw, 1200px"
                      priority
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                )}

                <article className="bg-[#FAF8F5] border border-[#193B2F]/10 p-6 md:p-10 shadow-sm prose prose-slate prose-lg max-w-none prose-headings:font-lora prose-headings:text-[#193B2F] prose-headings:font-normal prose-p:font-sans prose-p:text-[#193B2F]/85 prose-p:leading-relaxed prose-strong:text-[#193B2F] prose-blockquote:border-[#8A724C] prose-blockquote:bg-[#F2ECE1] prose-blockquote:py-2">
                  <div
                    className="webview space-y-4 font-sans font-normal text-[#193B2F]/90 text-justify leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: news.content }}
                  />
                </article>

                <div className="mt-12 pt-8 border-t border-[#193B2F]/10 flex flex-wrap gap-4 items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {["DragonHome", "NhaPho", "CanHo", "DauTu", "KhuDoThi"].map(
                      (tag) => (
                        <span
                          key={tag}
                          className="px-3.5 py-1.5 bg-[#F2ECE1] text-[#193B2F]/80 text-xs font-sans font-semibold tracking-wider uppercase border border-[#193B2F]/10 hover:border-[#C9AB81] hover:text-[#193B2F] transition-all cursor-pointer"
                        >
                          #{tag}
                        </span>
                      ),
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-[#8A724C] uppercase tracking-wider font-sans">
                      {dict.share || "Chia sẻ"}:
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleShareClick("fb")}
                        className="w-9 h-9 rounded-full bg-[#FAF8F5] border border-[#193B2F]/20 flex items-center justify-center text-[#193B2F] hover:bg-[#E5D4B2] hover:border-[#C9AB81] transition-all shadow-sm cursor-pointer"
                        title="Chia sẻ Facebook"
                      >
                        <Facebook className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleShareClick("tw")}
                        className="w-9 h-9 rounded-full bg-[#FAF8F5] border border-[#193B2F]/20 flex items-center justify-center text-[#193B2F] hover:bg-[#E5D4B2] hover:border-[#C9AB81] transition-all shadow-sm cursor-pointer"
                        title="Chia sẻ Twitter"
                      >
                        <Twitter className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleShareClick("copy")}
                        className="w-9 h-9 rounded-full bg-[#FAF8F5] border border-[#193B2F]/20 flex items-center justify-center text-[#193B2F] hover:bg-[#E5D4B2] hover:border-[#C9AB81] transition-all shadow-sm cursor-pointer"
                        title="Sao chép liên kết"
                      >
                        <LinkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>

              <aside className="hidden lg:block">
                <div className="sticky top-28 bg-[#FAF8F5] p-6 border border-[#193B2F]/10 shadow-sm">
                  <SameNews without={news._id} />
                </div>
              </aside>
            </div>
          </div>
        </main>
      </div>

      <div id="dang-ky-bao-gia">
        <FooterContact data={dynamicFooterContactData} />
      </div>
    </>
  );
}
